<template>
  <div class="flow-page">
    <Card>
      <template #title>
        <div class="title-row">
          <span class="title-main">退票</span>
          <Tag color="blue">办理须知以车站及12306.cn公告为准</Tag>
        </div>
      </template>
      <Steps :current="state.currentStep">
        <Step title="选择车票" />
        <Step title="核对信息" />
        <Step title="办理完成" />
      </Steps>
    </Card>

    <Card v-if="state.currentStep === 0" title="请选择需办理退票的电子客票">
      <Table
        :columns="orderColumns"
        :data-source="state.orderList"
        :pagination="false"
        row-key="orderSn"
      >
        <template #status="{ text }">
          <Tag :color="text === 10 ? 'green' : 'orange'">
            {{ text === 10 ? '可办理退票' : '办理中' }}
          </Tag>
        </template>
        <template #action="{ record }">
          <Button type="link" @click="chooseOrder(record)">退票</Button>
        </template>
      </Table>
    </Card>

    <Card v-if="state.currentStep === 1" title="核对退票信息">
      <Alert
        type="warning"
        show-icon
        message="退票费按梯次核收，应退票款以办理时系统计算为准；票款将按原支付渠道退回，到账时间以支付机构为准。"
      />
      <div class="order-summary">
        <span>订单号：{{ state.currentOrder?.orderSn }}</span>
        <span>车次：{{ state.currentOrder?.trainNumber }} 次</span>
        <span>{{ state.currentOrder?.departure }} — {{ state.currentOrder?.arrival }}</span>
      </div>
      <CheckboxGroup v-model:value="state.selectedPassengerIds" class="passenger-list">
        <Checkbox
          v-for="item in state.currentOrder?.passengers || []"
          :key="item.id"
          :value="item.id"
        >
          {{ item.realName }}（{{ item.ticketType }}，票价￥{{ item.amount }}）
        </Checkbox>
      </CheckboxGroup>

      <div class="fee-box">
        <Spin :spinning="state.refundFeeLoading">
          <RefundRescheduleFeePanel
            v-if="state.refundFeeData?.available"
            mode="refund"
            title="退票费用"
            compact
            :pending-selection="selectedRefundFee.pendingSelection"
            :ticket-price-yuan="selectedRefundFee.ticketPriceYuan"
            :refund-fee-yuan="selectedRefundFee.refundFeeYuan ?? 0"
            :refund-amount-yuan="selectedRefundFee.refundAmountYuan ?? 0"
            @show-rules="state.rulesModalVisible = true"
          />
          <div
            v-else-if="!state.refundFeeLoading && state.refundFeeTried"
            class="fee-unavailable"
          >
            {{ state.refundFeeData?.message || '暂无法获取费用，具体费用请联系客服' }}
          </div>
        </Spin>
      </div>

      <div class="action-row">
        <Button @click="state.currentStep = 0">返回</Button>
        <Button type="primary" :disabled="!state.selectedPassengerIds.length" @click="nextStep">
          确认退票
        </Button>
      </div>
    </Card>

    <Card v-if="state.currentStep === 2" title="退票办理">
      <Result
        status="success"
        title="退票受理成功"
        sub-title="票款将按规定退回原支付渠道，具体到账时间以银行或支付机构为准。"
      >
        <template #extra>
          <Space>
            <Button @click="resetFlow">继续办理退票</Button>
            <Button type="primary" @click="router.push('/ticketList')">返回订单列表</Button>
          </Space>
        </template>
      </Result>
      <Timeline>
        <TimelineItem color="green">业务流水号：{{ state.refundSn }}</TimelineItem>
        <TimelineItem color="blue">票款预计 1～15 个工作日内到账</TimelineItem>
        <TimelineItem>如有疑问请致电铁路客服热线 12306</TimelineItem>
      </Timeline>
    </Card>
    <RefundRescheduleRulesModal v-model:visible="state.rulesModalVisible" />
  </div>
</template>

<script setup>
import {
  Card,
  Tag,
  Steps,
  Step,
  Table,
  Button,
  CheckboxGroup,
  Checkbox,
  Alert,
  Result,
  Space,
  Timeline,
  TimelineItem,
  message,
  Spin
} from 'ant-design-vue'
import { computed, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { fetchRefundOrderList, fetchRefundTicket, fetchRefundFeeEstimate } from '@/service'
import RefundRescheduleFeePanel from '@/components/refund-reschedule/RefundRescheduleFeePanel.vue'
import RefundRescheduleRulesModal from '@/components/refund-reschedule/RefundRescheduleRulesModal.vue'
import Cookie from 'js-cookie'
import { sumSelectedRefundFee } from '@/utils/refundFeeEstimate'

const router = useRouter()
const userId = Cookie.get('userId')

const state = reactive({
  currentStep: 0,
  refundSn: '',
  orderList: [],
  currentOrder: null,
  selectedPassengerIds: [],
  rulesModalVisible: false,
  refundFeeLoading: false,
  refundFeeTried: false,
  refundFeeData: null
})

const orderColumns = [
  { title: '订单号', dataIndex: 'orderSn' },
  { title: '车次', dataIndex: 'trainNumber' },
  { title: '出发站', dataIndex: 'departure' },
  { title: '到达站', dataIndex: 'arrival' },
  { title: '状态', dataIndex: 'status', slots: { customRender: 'status' } },
  { title: '操作', key: 'action', slots: { customRender: 'action' } }
]

const selectedRefundFee = computed(() =>
  sumSelectedRefundFee(
    state.refundFeeData,
    state.selectedPassengerIds,
    state.currentOrder?.passengers || []
  )
)

const loadRefundFeeEstimate = (orderSn) => {
  state.refundFeeLoading = true
  state.refundFeeTried = false
  state.refundFeeData = null
  fetchRefundFeeEstimate({ orderSn })
    .then((res) => {
      state.refundFeeData = res?.data ?? null
    })
    .catch(() => {
      state.refundFeeData = {
        available: false,
        message: '暂无法获取费用，具体费用请联系客服'
      }
    })
    .finally(() => {
      state.refundFeeLoading = false
      state.refundFeeTried = true
    })
}

const chooseOrder = (record) => {
  state.currentOrder = record
  state.selectedPassengerIds = []
  state.currentStep = 1
  loadRefundFeeEstimate(record.orderSn)
}

const nextStep = async () => {
  try {
    // 构造退票请求参数
    const refundParams = {
      orderSn: state.currentOrder.orderSn,
      passengerIds: state.selectedPassengerIds
    }

    const res = await fetchRefundTicket(refundParams)
    if (res.code === '00000') {
      state.refundSn = res.data?.refundSn || `TK${Date.now()}`
      state.currentStep = 2
      message.success('退票申请已提交')
    } else {
      message.error(res.message || '退票申请失败')
    }
  } catch (error) {
    console.error('退票申请失败:', error)
    message.error('网络异常，请稍后重试')
  }
}

const resetFlow = () => {
  state.currentStep = 0
  state.currentOrder = null
  state.selectedPassengerIds = []
  state.refundSn = ''
}

// 获取退票订单列表
const loadRefundOrders = async () => {
  try {
    const res = await fetchRefundOrderList({
      userId,
      current: 1,
      size: 10
    })
    if (res.code === '0') {
      state.orderList = res.data?.records || []
    } else {
      message.error(res.message || '获取订单列表失败')
    }
  } catch (error) {
    console.error('获取退票订单失败:', error)
    message.error('网络异常，请稍后重试')
  }
}

onMounted(() => {
  loadRefundOrders()
})
</script>

<style lang="scss" scoped>
.flow-page {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-main {
  font-weight: 600;
}

.order-summary {
  margin: 14px 0;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border: 1px solid #e6edf8;
  border-radius: 10px;
  background: #f8fbff;
}

.passenger-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.fee-box {
  border: 1px dashed #d9e5f7;
  border-radius: 10px;
  padding: 12px;
  background: #fafcff;
  line-height: 2;
}

.money {
  font-weight: 700;
  color: #1d4ed8;
}

.money.warn {
  color: #ea580c;
}

.money.success {
  color: #16a34a;
}

.fee-unavailable {
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  background: #f7f8fa;
  color: #999;
  font-size: 13px;
  text-align: center;
}

.action-row {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
